<script setup lang="ts">
import { computed } from 'vue';
import { NxSelectInput } from '.';
import { NxStatusIcon, type StatusValue } from '../Branding';
import { InputHeight } from './internal';

/** The sales-document lifecycle — the set this input offered exclusively until I-622. */
const DOCUMENT_STATUSES: StatusValue[] = ['draft', 'open', 'accepted', 'declined'];

const props = defineProps<{
  id?: string;
  height?: InputHeight;
  placeholder: string;
  multiple?: boolean;
  disabled?: boolean;
  defaultValue?: StatusValue[];
  /**
   * Which statuses to offer. Defaults to the sales-document lifecycle, so every
   * existing caller keeps its list unchanged. Records with their own status set
   * pass theirs — the CRM Kundenakte offers `['open', 'done']` — because offering
   * `accepted`/`declined` on a file that can only be open or done produces a
   * filter nobody can use.
   */
  options?: StatusValue[];
  /**
   * Plain-text twin of a status. This input renders each option as an icon, so
   * without it the tooltip and the typeahead fall back to the wire values
   * (`draft`, `open`) — text no reader of this field ever sees. The kit has no
   * translations of its own, so the consumer supplies them.
   */
  optionText?: (option: StatusValue) => string;
}>();

const statusOptions = computed(() => (props.options?.length ? props.options : DOCUMENT_STATUSES));

const [modelValue, modifier] = defineModel<StatusValue[] | [null], 'lazy'>({
  required: true,
});
</script>

<template>
  <NxSelectInput
    v-if="modifier.lazy"
    v-model.lazy="modelValue"
    v-bind="{ ...props, options: statusOptions }"
  >
    <template #placeholder>
      <div class="placeholder"></div>
    </template>
    <template #option="{ option }">
      <NxStatusIcon :status="option" />
    </template>
  </NxSelectInput>
  <NxSelectInput v-else v-model="modelValue" v-bind="{ ...props, options: statusOptions }">
    <template #placeholder>
      <div class="placeholder"></div>
    </template>
    <template #option="{ option }">
      <NxStatusIcon :status="option" />
    </template>
  </NxSelectInput>
</template>

<style scoped>
.placeholder {
  display: flex;
  width: 1lh;
  height: 1lh;
  border-radius: var(--nxt-radius-sm);
  background-color: var(--nxt-fg-muted);
}
</style>
